import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Edit, Trash2, ExternalLink } from "lucide-react";
import usePageTitle from "@/hooks/usePageTitle";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface AppListing {
  id: string;
  name: string;
  description: string;
  logo_url: string | null;
  visit_url: string;
  approved: boolean;
  created_at: string | null;
  user_id: string;
  url_slug: string;
}

const MyListings = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [listings, setListings] = useState<AppListing[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [appToDelete, setAppToDelete] = useState<string | null>(null);

  usePageTitle('My Listings');

  useEffect(() => {
    const setupAuth = async () => {
      await checkAuthStatus();
      
      const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
        console.log("Auth state changed in MyListings:", event, "Session exists:", !!session);
        if (session) {
          console.log("User ID from auth state change:", session.user.id);
          setUserId(session.user.id);
          await loadUserListings(session.user.id);
        } else if (event === 'SIGNED_OUT') {
          navigate('/auth');
        }
      });
      
      return () => {
        authListener.subscription.unsubscribe();
      };
    };
    
    setupAuth();
  }, []);

  const checkAuthStatus = async () => {
    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }
      
      console.log("User authenticated in MyListings, user ID:", session.user.id);
      setUserId(session.user.id);
      await loadUserListings(session.user.id);
    } catch (error) {
      console.error('Error checking auth status:', error);
      toast({
        title: "Error checking authentication",
        description: "There was a problem verifying your login status. Please try logging in again.",
        variant: "destructive",
      });
      navigate("/auth");
    } finally {
      setIsLoading(false);
    }
  };

  const loadUserListings = async (userId: string) => {
    if (!userId) {
      console.error("Cannot load listings: userId is null");
      return;
    }
    
    try {
      console.log("Loading listings for user ID:", userId);
      const { data, error } = await supabase
        .from('apps')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error("Error fetching user listings:", error);
        throw error;
      }
      
      console.log("Fetched listings:", data);
      setListings(data || []);
    } catch (error: any) {
      console.error('Error loading listings:', error);
      toast({
        title: "Error loading your listings",
        description: error.message || "There was a problem fetching your listings.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteClick = (appId: string) => {
    setAppToDelete(appId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!appToDelete) return;
    
    try {
      await supabase.from('app_categories').delete().eq('app_id', appToDelete);
      await supabase.from('app_platforms').delete().eq('app_id', appToDelete);
      await supabase.from('app_sports').delete().eq('app_id', appToDelete);
      
      const { error } = await supabase.from('apps').delete().eq('id', appToDelete);
      
      if (error) throw error;
      
      toast({
        title: "App deleted",
        description: "Your app has been successfully deleted.",
      });
      
      if (userId) loadUserListings(userId);
    } catch (error: any) {
      console.error('Error deleting app:', error);
      toast({
        title: "Error deleting app",
        description: error.message || "There was a problem deleting your app.",
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setAppToDelete(null);
    }
  };

  const handleEditClick = (appId: string) => {
    navigate(`/edit-app/${appId}`);
  };
  
  const handleVisitClick = (urlSlug: string) => {
    navigate(`/tool/${urlSlug}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navigation />
        <div className="container py-12 flex justify-center items-center flex-grow">
          <p>Loading your listings...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navigation />
      <div className="container py-8 flex-grow">
        <h1 className="text-2xl font-bold mb-6">My Listings</h1>
        
        {listings.length === 0 ? (
          <div className="bg-white rounded-lg border p-8 text-center">
            <p className="text-gray-600 mb-4">You haven't submitted any apps yet.</p>
            <Button onClick={() => navigate('/submit-app')}>
              Submit Your First App
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {listings.map((app) => (
              <div key={app.id} className="bg-white rounded-lg border p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <img 
                    src={app.logo_url || "https://placehold.co/60x60"} 
                    alt={app.name} 
                    className="w-12 h-12 rounded-md object-contain bg-gray-50" 
                  />
                  <div>
                    <h2 className="font-semibold">{app.name}</h2>
                    <p className="text-sm text-gray-600 truncate max-w-md">{app.description}</p>
                    <div className="mt-1 text-xs">
                      <span className={`px-2 py-0.5 rounded-full ${app.approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {app.approved ? 'Approved' : 'Pending Approval'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleVisitClick(app.url_slug || app.id)}
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Visit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleEditClick(app.id)}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleDeleteClick(app.id)}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <Footer />
      
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your app listing and remove it from our database.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 text-white hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MyListings;
